// GeoLocationPicker.js: A React component for picking geolocations using Leaflet map. Used in Work Location configuration.
import {
  MapContainer,
  TileLayer,
  Marker,
  Circle,
  useMapEvents,
  useMap,
  Popup,
} from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useRef, useState } from "react";
// Fix marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

/* Handles map click */
const MapClickHandler = ({ onSelect, onPopup }) => {
  useMapEvents({
    async click(e) {
      const lat = e.latlng.lat.toFixed(6);
      const lng = e.latlng.lng.toFixed(6);
      onPopup("Loading…");
      onSelect(`${lat},${lng}`);

      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
        );
        const data = await res.json();

        if (data?.display_name) {
          onPopup(data.display_name);
        } else {
          onPopup(`Lat: ${lat}, Lng: ${lng}`);
        }
      } catch (err) {
        onPopup(`Lat: ${lat}, Lng: ${lng}`);
      }
    },
  });

  return null;
};

/* Re-centers map when location changes */
const MapRecenter = ({ position, zoom }) => {
  const map = useMap();
  const currentZoom = map.getZoom();

  if (currentZoom < zoom) {
    map.setView(position, zoom);
  } else {
    map.panTo(position);
  }

  return null;
};

const GeoLocationPicker = ({
  value,
  radius,
  onChange,
  maxheight = 300,
  expand,
  setExpand,
}) => {
  const markerRef = useRef(null);
  const [locating, setLocating] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState("");
  const [popupText, setPopupText] = useState("");
  const position = value ? value.split(",").map(Number) : [20.5937, 78.9629]; // India center

  const handleCurrentLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by this browser");
      return;
    }

    setLocating(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude, accuracy } = pos.coords;
        const lat = latitude.toFixed(6);
        const lng = longitude.toFixed(6);

        // ⚠️ Accuracy warning
        if (accuracy > 100) {
          alert(
            `Location accuracy is low (~${Math.round(
              accuracy
            )}m). Please verify or adjust on map.`
          );
        }
        onChange(`${lat},${lng}`);

        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
          );
          const data = await res.json();

          if (data?.display_name) {
            setPopupText(data.display_name);
          } else {
            setPopupText(`Lat: ${lat}, Lng: ${lng}`);
          }
        } catch (err) {
          setPopupText(`Lat: ${lat}, Lng: ${lng}`);
        }

        setLocating(false);
      },
      () => {
        alert("Unable to fetch current location");
        setLocating(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
      }
    );
  };

  const handleSearch = async () => {
    if (!searchText) return;
    setNotFound("");
    setSearching(true);

    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(
          searchText
        )}&format=json&limit=1`
      );
      const data = await res.json();
      if (data?.length > 0) {
        onChange(`${data[0].lat},${data[0].lon}`);
        setPopupText(data[0].display_name);
      } else {
        setNotFound("Location not found");
      }
    } catch (err) {
      setNotFound("Error searching location");
    } finally {
      setSearching(false);
    }
  };
  useEffect(() => {
    if (markerRef.current) {
      markerRef.current.openPopup();
    }
  }, [value, popupText]);

  return (
    <Box>
      {/* Current Location Button */}
      <Box
        mb={1}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "start",
          gap: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "start",
            gap: 2,
            flexGrow: 1,
          }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder="Search place, area, landmark"
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              notFound && setNotFound("");
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (!searching) {
                  handleSearch();
                }
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 32,
              },
              "& input": {
                padding: "4px 10px",
              },
            }}
            helperText={notFound ? notFound : ""}
            FormHelperTextProps={{
              sx: {
                marginLeft: "4px",
                fontSize: "13px",
                fontWeight: 500,
              },
            }}
          />

          <Button
            size="small"
            variant="outlined"
            onClick={handleSearch}
            disabled={searching}
            sx={{ 
              height: 31,
              width: 85,
              px: 1,
              "& .MuiButton-startIcon" : {
                marginRight: 0.3,
              }
             }}
            startIcon={
              searching ? (
                <CircularProgress size={18} disableShrink />
              ) : (
                <SearchIcon fontSize="small" />
              )
            }
          >
            Search
          </Button>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "start",
          }}
        >
          <Tooltip
            title={
              locating ? "Fetching current location…" : "Use Current Location"
            }
          >
            <IconButton size="small" onClick={handleCurrentLocation}>
              {locating ? (
                <CircularProgress size={20} disableShrink />
              ) : (
                <MyLocationIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          <Tooltip title={expand ? "Collapse Map" : "Expand Map"}>
            <IconButton size="small" onClick={() => setExpand((prev) => !prev)}>
              {expand ? <FullscreenExitIcon /> : <FullscreenIcon />}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <MapContainer
        center={position}
        zoom={value ? 15 : 5}
        style={{ height: maxheight, width: "100%" }}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        <MapClickHandler onSelect={onChange} onPopup={setPopupText} />
        <MapRecenter position={position} zoom={value ? 15 : 5} />

        {value && (
          <>
            <Marker position={position} ref={markerRef}>
              <Popup>
                <Box sx={{ fontSize: 13, maxWidth: 220 }}>
                  {popupText || (
                    <>
                      Lat: {position[0].toFixed(6)} <br />
                      Lng: {position[1].toFixed(6)}
                    </>
                  )}
                </Box>
              </Popup>
            </Marker>
            {radius && (
              <Circle
                center={position}
                radius={Number(radius)}
                pathOptions={{
                  color: "#8b44ff",
                  fillOpacity: 0.2,
                }}
              />
            )}
          </>
        )}
      </MapContainer>
    </Box>
  );
};

export default GeoLocationPicker;
